import { useCallback, useEffect, useState } from 'react'
import { UsersRound, X, Building2, Loader2, SquarePen, Trash2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '../../auth/store/authStore'
import { teamsService } from '../../../services/teamsService'
import { propertiesService } from '../../../services/propertiesService'
import type { Team, Property } from '../../../types/api.types'
import { EmptyState } from './EmptyState'
import { LoadErrorState } from '../../../components/LoadErrorState'
import { Tooltip } from '../../../components/Tooltip'
import { useSelectedPropertyId, useSelectedPropertyName } from '../../../store/propertyStore'

interface TeamFormState {
  name: string
  propertyId: number | undefined
  type: string
}

const EMPTY_FORM: TeamFormState = { name: '', propertyId: undefined, type: '' }

export function TeamsTab(): JSX.Element {
  const authUser = useAuthStore((s) => s.user)

  const [teams, setTeams] = useState<Team[]>([])
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const selectedPropertyId = useSelectedPropertyId()
  const selectedPropertyName = useSelectedPropertyName()

  const [showModal, setShowModal] = useState(false)
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null)
  const [form, setForm] = useState<TeamFormState>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)

  const loadData = useCallback(async (propFilter?: number) => {
    setLoading(true)
    setLoadError(false)
    try {
      const [allTeams, allProps] = await Promise.all([
        teamsService.getAll(propFilter),
        propertiesService.getAll(),
      ])
      setTeams(allTeams)
      setProperties(allProps)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData(selectedPropertyId ?? undefined) }, [loadData, selectedPropertyId])

  function resetForm() {
    setForm(EMPTY_FORM)
    setEditingTeamId(null)
  }

  function openCreateModal() {
    resetForm()
    setShowModal(true)
  }

  function openEditModal(team: Team) {
    setForm({
      name: team.name,
      propertyId: team.property_id,
      type: team.type || '',
    })
    setEditingTeamId(team.id)
    setShowModal(true)
  }

  async function handleSubmit() {
    if (!form.name.trim()) { toast.error('Name is required'); return }
    if (!editingTeamId && !form.propertyId) { toast.error('Property is required'); return }
    setSubmitting(true)
    try {
      if (!editingTeamId) {
        await teamsService.create({
          name: form.name.trim(),
          property_id: form.propertyId!,
          organization_id: authUser?.organization_id ?? 0,
          type: form.type.trim() || undefined,
        })
        toast.success('Team created successfully')
      } else {
        await teamsService.update(editingTeamId, {
          name: form.name.trim(),
          type: form.type.trim() || undefined,
        })
        toast.success('Team updated successfully')
      }
      setShowModal(false)
      resetForm()
      await loadData(selectedPropertyId ?? undefined)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save team'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) return
    try {
      await teamsService.delete(id)
      toast.success('Team deleted successfully')
      await loadData(selectedPropertyId ?? undefined)
    } catch {
      toast.error('Failed to delete team')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-brand-green" />
        <span className="ml-2 text-sm text-gray-500">Loading teams...</span>
      </div>
    )
  }

  if (loadError) {
    return <LoadErrorState message="Couldn't load teams" onRetry={() => loadData(selectedPropertyId ?? undefined)} />
  }

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-semibold text-gray-700">Viewing: {selectedPropertyName ?? 'All Properties'}</p>

        <button
          type="button"
          onClick={openCreateModal}
          className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-90"
        >
          Create Team
        </button>
      </div>

      {teams.length === 0 ? (
        <EmptyState icon={UsersRound} message="No teams found" hint={selectedPropertyId ? 'No teams in the selected property' : undefined} />
      ) : (
        <>
        {/* Mobile card list */}
        <div className="space-y-3 md:hidden">
          {teams.map(t => (
            <div key={t.id} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-gray-900">{t.name}</p>
                  <p className="truncate text-[13px] text-gray-500">{properties.find(p => p.id === t.property_id)?.name || 'N/A'}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => openEditModal(t)} aria-label={`Edit ${t.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-brand-green hover:bg-brand-green/10">
                    <SquarePen size={16} />
                  </button>
                  <button type="button" onClick={() => handleDelete(t.id)} aria-label={`Delete ${t.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-red-400 hover:bg-red-50">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              {t.type && (
                <div className="mt-3">
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-[12px] font-medium text-gray-700">{t.type}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden overflow-x-auto rounded-lg border border-gray-200 md:block">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">ID</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Name</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Property Name</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Type</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((t, idx) => (
                <tr key={t.id} className="border-t border-gray-100">
                  <td className="px-6 py-4 text-center text-[14px] text-gray-700">{String(idx + 1).padStart(2, '0')}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-gray-900">{t.name}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-gray-700">{properties.find(p => p.id === t.property_id)?.name || 'N/A'}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-gray-700">{t.type || 'N/A'}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <Tooltip label="Edit team">
                        <button type="button" onClick={() => openEditModal(t)} className="text-brand-green hover:text-brand-green/70">
                          <SquarePen size={16} />
                        </button>
                      </Tooltip>
                      <Tooltip label="Delete team">
                        <button type="button" onClick={() => handleDelete(t.id)} className="text-red-400 hover:text-red-600">
                          <Trash2 size={16} />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {editingTeamId ? 'Edit Team' : 'Create Team'}
              </h2>
              <button type="button" onClick={() => { setShowModal(false); resetForm() }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Name*</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="e.g Kitchen Team."
                />
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Property*</label>
                <div className="relative">
                  <Building2 size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                    value={form.propertyId ?? ''}
                    onChange={e => setForm({ ...form, propertyId: Number(e.target.value) })}
                    disabled={!!editingTeamId}
                    className="w-full rounded-lg border border-gray-200 py-2.5 pl-9 pr-3 text-[14px] text-gray-900 outline-none focus:border-brand-green disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="" disabled>Select your Branch</option>
                    {properties.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                {editingTeamId && (
                  <p className="mt-1 text-[11px] text-gray-400">Property cannot be changed after creation.</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Type (Optional)</label>
                <input
                  type="text"
                  value={form.type}
                  onChange={e => setForm({ ...form, type: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="e.g Kitchen, Housekeeping, Reception."
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : editingTeamId ? 'Edit' : 'Create Team'}
              </button>
              <button
                type="button"
                onClick={() => { setShowModal(false); resetForm() }}
                className="flex-1 rounded-lg border border-gray-300 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
